import React, { ChangeEvent, FC, FormEvent } from "react";
import noop from "../../utils/noop";
import Row from "../Row";
import { ChoiceBranchingRule, ChoiceBranchingDestination } from "../../models";
import Checkbox from "../Checkbox";
import Column from "../Column";
import Required from "../Required";
import { setDestination } from "../../utils/choiceQuestionUtils";

export const ChoiceBranchingRuleDefaultValue: ChoiceBranchingRule = {
  defaultQuestionId: "",
  destinationQuestionId: "",
  expectedAnswerValues: [],
};

export const PerChoiceBranchingRuleDefaultValue: ChoiceBranchingRule = {
  defaultQuestionId: "",
  destinationQuestionId: null,
  expectedAnswerValues: null,
  destinations: [],
};

interface ChoiceBranchingRuleProps {
  value?: ChoiceBranchingRule;
  surveyQuestionIDs: string[];
  choiceQuestionIDs: string[];
  onChange?: (value: ChoiceBranchingRule) => any;
}

/**
 * Edits the legacy choice branching rule.
 *
 * Multiple selected choices share one destination, while choices that are not
 * selected use `defaultQuestionId`. This is the branching editor for the
 * original `expectedAnswerValues` and `destinationQuestionId` model.
 */
export const ChoiceBranchingRuleComponent: FC<ChoiceBranchingRuleProps> = (
  props
) => {
  const {
    value = ChoiceBranchingRuleDefaultValue,
    onChange = noop,
  } = props;

  function checkboxChoice(item: string, selectedItems: string[]) {
    onChange({
      ...value,
      expectedAnswerValues: selectedItems,
    });
  }

  function selectDefault(event: FormEvent<HTMLSelectElement>) {
    onChange({
      ...value,
      defaultQuestionId: event.currentTarget.value,
    });
  }

  function selectDestination(event: FormEvent<HTMLSelectElement>) {
    onChange({
      ...value,
      destinationQuestionId: event.currentTarget.value,
    });
  }

  // set up values before return
  let ifPrompt = <span>If choices</span>;
  let skipPrompt = <span>are selected, go to question</span>;
  if (props.choiceQuestionIDs.length < 2) {
    ifPrompt = <span>If choice</span>;
    skipPrompt = <span>is selected, go to question</span>;
  }

  return (
    <Column rowGap={"5px"}>
      <Row gap={".5em"}>
        {ifPrompt}
        <Checkbox
          inputList={props.choiceQuestionIDs}
          selectedItems={value.expectedAnswerValues?.filter(
            (choiceId): choiceId is string => choiceId !== null
          )}
          onChange={checkboxChoice}
        />
      </Row>
      <Row gap={".5em"}>
        {skipPrompt}
        <Required>
          <label>
            <select
              onChange={selectDestination}
              value={value.destinationQuestionId ?? ""}
            >
              <option value="">---SELECT QUESTION---</option>
              {props.surveyQuestionIDs.map((id, idx) => {
                return (
                  <option key={"survey" + id + idx} value={id}>
                    {id}
                  </option>
                );
              })}
              <option value={"completionPage"}>Completion Page</option>
            </select>
          </label>
        </Required>
      </Row>
      <Row gap={".5em"}>
        <span>Otherwise go to</span>
        <Required>
          <label>
            <select onChange={selectDefault} value={value.defaultQuestionId}>
              <option value="">---SELECT QUESTION---</option>
              {props.surveyQuestionIDs.map((id, idx) => {
                return (
                  <option key={"survey" + id + idx} value={id}>
                    {id}
                  </option>
                );
              })}
              <option value={"completionPage"}>Completion Page</option>
            </select>
          </label>
        </Required>
      </Row>
    </Column>
  );
};

interface PerChoiceBranchingRuleProps {
  value: ChoiceBranchingRule;
  surveyQuestionIDs: string[];
  choiceQuestionIDs: string[];
  onChange: (value: ChoiceBranchingRule) => any;
}

/**
 * Edits per-choice branching for a single-select choice question.
 *
 * Each choice gets its own destination in `destinations`. This should not be
 * used for multiple-select questions.
 */
export const PerChoiceBranchingRuleComponent: FC<PerChoiceBranchingRuleProps> = ({
  value,
  surveyQuestionIDs,
  choiceQuestionIDs,
  onChange,
}) => {
  function selectDestination(choiceId: string) {
    return (event: FormEvent<HTMLSelectElement>) => {
      onChange(setDestination(value, choiceId, event.currentTarget.value));
    };
  }

  function selectDefault(event: FormEvent<HTMLSelectElement>) {
    onChange({
      ...value,
      defaultQuestionId: event.currentTarget.value,
    });
  }

  function toggleChoiceDestination(choiceId: string) {
    return (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.currentTarget.checked
        ? setDestination(value, choiceId, "")
        : {
            ...value,
            destinations: value.destinations!.filter(
              (destination): destination is ChoiceBranchingDestination =>
                destination !== null && destination.choiceId !== choiceId
            ),
          }
      );
    };
  }

  const destinationFor = (choiceId: string) =>
    value.destinations!.find(
      (destination): destination is ChoiceBranchingDestination =>
        destination !== null && destination.choiceId === choiceId
    );

  return (
    <Column rowGap={"5px"}>
      {choiceQuestionIDs.map((choiceId, index) => {
        const destinationId = `choice-destination-${index}`;
        const destination = destinationFor(choiceId);
        return (
        <Row gap={".5em"} key={`${choiceId}-${index}`}>
          <label>
            <input
              type="checkbox"
              checked={destination !== undefined}
              onChange={toggleChoiceDestination(choiceId)}
            />
            {choiceId}
          </label>
          {destination && <>
            <span>is selected, go to question</span>
            <select
              id={destinationId}
              aria-label={`Destination when choice ${choiceId} is selected`}
              onChange={selectDestination(choiceId)}
              value={destination.destinationQuestionId}
            >
              <option value="">---SELECT QUESTION---</option>
              {surveyQuestionIDs.map((id, idx) => (
                <option key={"survey" + id + idx} value={id}>
                  {id}
                </option>
              ))}
              <option value="completionPage">Completion Page</option>
            </select>
          </>}
        </Row>
        );
      })}
      <Row gap={".5em"}>
        <span>Otherwise go to</span>
        <Required>
          <label htmlFor="choice-default-destination">Destination</label>
        </Required>
        <select
          id="choice-default-destination"
          onChange={selectDefault}
          value={value.defaultQuestionId}
        >
          <option value="">---SELECT QUESTION---</option>
          {surveyQuestionIDs.map((id, idx) => (
            <option key={"survey" + id + idx} value={id}>
              {id}
            </option>
          ))}
          <option value="completionPage">Completion Page</option>
        </select>
      </Row>
    </Column>
  );
};
